import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getPresentationById,
  updatePresentation,
  uploadPresentationAsset
} from '../services/presentationsService';
import {
  listSlidesByPresentation,
  createSlide,
  updateSlide,
  deleteSlide,
  reorderSlides
} from '../services/presentationSlidesService';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiPlus, FiTrash2, FiSave, FiPlay, FiImage, FiVideo, FiFile, FiCode } = FiIcons;

const CONTENT_TYPES = [
  { value: 'image', label: 'Image', icon: FiImage },
  { value: 'video', label: 'Video URL', icon: FiVideo },
  { value: 'pdf', label: 'PDF', icon: FiFile },
  { value: 'html', label: 'HTML', icon: FiCode }
];

export default function PresentationEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, authReady, loading: authLoading, profileLoading } = useAuth();
  const [presentation, setPresentation] = useState(null);
  const [slides, setSlides] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const role = profile?.role ?? null;
  const userId = profile?.id ?? null;
  const opts = role && userId ? { role, userId } : {};
  const currentSlide = slides[selectedIndex] || null;

  const [editDraft, setEditDraft] = useState(null);
  useEffect(() => {
    setEditDraft(currentSlide ? { ...currentSlide } : null);
  }, [currentSlide?.id, selectedIndex]);

  const load = useCallback(async () => {
    if (!id || id === 'new' || !userId) return;
    setLoading(true);
    const requestOpts = { role, userId };
    const [presRes, slidesRes] = await Promise.all([
      getPresentationById(id, requestOpts),
      listSlidesByPresentation(id, requestOpts)
    ]);
    if (presRes.error || !presRes.data) {
      toast.error(presRes.error?.message || 'Presentation not found');
      setPresentation(null);
      setSlides([]);
      setLoading(false);
      return;
    }
    setPresentation(presRes.data);
    setSlides(slidesRes.data || []);
    setSelectedIndex(0);
    setLoading(false);
  }, [id, role, userId]);

  useEffect(() => {
    if (!authReady || !userId || (role !== 'agent' && role !== 'admin')) return;
    if (id === 'new') {
      setLoading(false);
      setPresentation(null);
      setSlides([]);
      return;
    }
    load();
  }, [authReady, userId, role, id, load]);

  const handleSavePresentation = async () => {
    if (!presentation || !id || id === 'new') return;
    setSaving(true);
    const { error } = await updatePresentation(id, {
      title: presentation.title,
      description: presentation.description,
      is_published: presentation.is_published,
      event_id: presentation.event_id || null,
      thumbnail_url: presentation.thumbnail_url
    }, opts);
    setSaving(false);
    if (error) {
      toast.error(error.message || 'Failed to save');
      return;
    }
    toast.success('Saved');
  };

  const handleAddSlide = async () => {
    if (!id || id === 'new') return;
    const nextNum = slides.length + 1;
    const { data, error } = await createSlide(
      { presentation_id: id, slide_number: nextNum, title: `Slide ${nextNum}`, content_type: 'html' },
      opts
    );
    if (error) {
      toast.error(error.message || 'Failed to add slide');
      return;
    }
    setSlides((prev) => [...prev, data].sort((a, b) => a.slide_number - b.slide_number));
    setSelectedIndex(slides.length);
    toast.success('Slide added');
  };

  const handleDeleteSlide = async (slideId) => {
    if (!slideId) return;
    const { error } = await deleteSlide(slideId, opts);
    if (error) {
      toast.error(error.message || 'Failed to delete');
      return;
    }
    const newSlides = slides.filter((s) => s.id !== slideId);
    setSlides(newSlides);
    setSelectedIndex((i) => Math.min(i, Math.max(0, newSlides.length - 1)));
    toast.success('Slide removed');
  };

  const handleUpdateSlide = async (slideId, updates) => {
    if (!slideId) return;
    const { error } = await updateSlide(slideId, updates, opts);
    if (error) {
      toast.error(error.message || 'Failed to update slide');
      return;
    }
    setSlides((prev) =>
      prev.map((s) => (s.id === slideId ? { ...s, ...updates } : s))
    );
  };

  const handleFileUpload = async (slideId, field, file) => {
    if (!id || id === 'new' || !file) return;
    setUploading(true);
    const { data, error } = await uploadPresentationAsset(id, file);
    setUploading(false);
    if (error) {
      toast.error(error.message || 'Upload failed');
      return;
    }
    await handleUpdateSlide(slideId, {
      content_url: data.url,
      ...(field === 'image' && data.thumbnail_url && { content_url: data.url })
    });
    toast.success('Uploaded');
  };

  const handleReorder = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= slides.length) return;
    const reordered = [...slides];
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, removed);
    const ordered = reordered.map((s, i) => ({ id: s.id, slide_number: i + 1 }));
    const { error } = await reorderSlides(id, ordered, opts);
    if (error) {
      toast.error(error.message || 'Reorder failed');
      return;
    }
    setSlides(reordered.map((s, i) => ({ ...s, slide_number: i + 1 })));
    setSelectedIndex(toIndex);
  };

  const onDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (e, toIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === toIndex) return;
    handleReorder(draggedIndex, toIndex);
    setDraggedIndex(null);
  };
  const onDragEnd = () => setDraggedIndex(null);

  if (!authReady || authLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00AAFF] border-t-transparent" />
      </div>
    );
  }

  if (profile?.role !== 'agent' && profile?.role !== 'admin') {
    navigate('/dashboard');
    return null;
  }

  if (id === 'new') {
    return (
      <div className="p-6 text-center text-gray-600">
        Create a presentation from the list page (New Presentation).
      </div>
    );
  }

  if (loading || !presentation) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00AAFF] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <div className="flex-shrink-0 flex items-center justify-between border-b bg-white px-4 py-2">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/presentations')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <input
            type="text"
            value={presentation.title || ''}
            onChange={(e) => setPresentation((p) => ({ ...p, title: e.target.value }))}
            className="text-lg font-semibold border-0 border-b border-transparent hover:border-gray-300 focus:border-[#00AAFF] focus:ring-0 bg-transparent"
            placeholder="Presentation title"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!presentation.is_published}
              onChange={(e) => setPresentation((p) => ({ ...p, is_published: e.target.checked }))}
              className="rounded"
            />
            Publish
          </label>
          <a
            href={`/presentations/${id}/view`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <SafeIcon icon={FiPlay} className="w-5 h-5" />
          </a>
          <button
            type="button"
            onClick={handleSavePresentation}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0090dd] disabled:opacity-50"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Left: slide list */}
        <div className="w-56 flex-shrink-0 border-r bg-gray-50 flex flex-col">
          <div className="p-2 border-b flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Slides</span>
            <button
              type="button"
              onClick={handleAddSlide}
              className="p-1.5 rounded hover:bg-gray-200"
              title="Add slide"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
            </button>
          </div>
          <ul className="flex-1 overflow-y-auto p-2 space-y-1">
            {slides.map((slide, index) => (
              <li
                key={slide.id}
                draggable
                onDragStart={(e) => onDragStart(e, index)}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, index)}
                onDragEnd={onDragEnd}
                onClick={() => setSelectedIndex(index)}
                className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${
                  selectedIndex === index ? 'bg-[#00AAFF]/15 border-[#00AAFF]' : 'bg-white border-gray-200 hover:bg-gray-100'
                }`}
              >
                <span className="text-xs text-gray-500 w-6">{index + 1}</span>
                <span className="flex-1 truncate text-sm">{slide.title || 'Untitled'}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDeleteSlide(slide.id); }}
                  className="p-1 text-gray-400 hover:text-red-600"
                >
                  <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Center: edit slide */}
        <div className="flex-1 overflow-y-auto p-6 bg-white">
          {!currentSlide ? (
            <div className="text-center text-gray-500 py-12">
              No slides. Add one from the left panel.
            </div>
          ) : (
            <div className="max-w-2xl space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slide title</label>
                <input
                  type="text"
                  value={editDraft?.title ?? ''}
                  onChange={(e) => setEditDraft((d) => (d ? { ...d, title: e.target.value } : null))}
                  onBlur={() => editDraft && handleUpdateSlide(currentSlide.id, { title: editDraft.title })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Slide title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content type</label>
                <div className="flex gap-2 flex-wrap">
                  {CONTENT_TYPES.map(({ value, label, icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setEditDraft((d) => (d ? { ...d, content_type: value } : null));
                        handleUpdateSlide(currentSlide.id, { content_type: value });
                      }}
                      className={`inline-flex items-center px-3 py-2 rounded-lg border text-sm ${
                        (editDraft?.content_type ?? currentSlide.content_type) === value
                          ? 'border-[#00AAFF] bg-[#00AAFF]/10 text-[#00AAFF]'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <SafeIcon icon={icon} className="w-4 h-4 mr-2" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {(editDraft?.content_type ?? currentSlide.content_type) === 'image' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(currentSlide.id, 'image', file);
                      e.target.value = '';
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#00AAFF]/10 file:text-[#00AAFF]"
                  />
                  {currentSlide.content_url && (
                    <img
                      src={currentSlide.content_url}
                      alt=""
                      className="mt-2 max-h-64 rounded-lg border object-contain"
                    />
                  )}
                </div>
              )}

              {(editDraft?.content_type ?? currentSlide.content_type) === 'video' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Embed video URL</label>
                  <input
                    type="url"
                    value={editDraft?.content_url ?? ''}
                    onChange={(e) => setEditDraft((d) => (d ? { ...d, content_url: e.target.value } : null))}
                    onBlur={() => editDraft && handleUpdateSlide(currentSlide.id, { content_url: editDraft.content_url })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="https://..."
                  />
                </div>
              )}

              {(editDraft?.content_type ?? currentSlide.content_type) === 'pdf' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PDF file</label>
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(currentSlide.id, 'pdf', file);
                      e.target.value = '';
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#00AAFF]/10 file:text-[#00AAFF]"
                  />
                  {currentSlide.content_url && (
                    <a
                      href={currentSlide.content_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 text-[#00AAFF] text-sm"
                    >
                      Open PDF
                    </a>
                  )}
                </div>
              )}

              {((editDraft?.content_type ?? currentSlide.content_type) === 'html' || !(editDraft?.content_type ?? currentSlide.content_type)) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HTML content</label>
                  <textarea
                    value={editDraft?.content_html ?? ''}
                    onChange={(e) => setEditDraft((d) => (d ? { ...d, content_html: e.target.value } : null))}
                    onBlur={() => editDraft && handleUpdateSlide(currentSlide.id, { content_html: editDraft.content_html })}
                    rows={10}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm"
                    placeholder="<p>Your content...</p>"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Speaker notes</label>
                <textarea
                  value={editDraft?.notes ?? ''}
                  onChange={(e) => setEditDraft((d) => (d ? { ...d, notes: e.target.value } : null))}
                  onBlur={() => editDraft && handleUpdateSlide(currentSlide.id, { notes: editDraft.notes })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Notes (not shown in view)"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
